/**
 * CausePro Theme Toggle
 * Day/Night theme switching for nonprofit themes
 *
 * Handles day/night theme switching with:
 * - localStorage persistence
 * - System preference detection
 * - Smooth transitions
 * - Accessibility support
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'causepro-theme';
  const DAY_CLASS = 'day';
  const NIGHT_CLASS = 'night';
  const TRANSITION_CLASS = 'theme-transition-active';
  const TRANSITION_DURATION = 400; // ms

  /**
   * Get the current theme from storage or system preference
   */
  function getStoredTheme() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      console.warn('localStorage not available:', e);
      return null;
    }
  }

  /**
   * Store theme preference
   */
  function storeTheme(theme) {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (e) {
      console.warn('Could not save theme preference:', e);
    }
  }

  /**
   * Get system color scheme preference
   */
  function getSystemPreference() {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return NIGHT_CLASS;
    }
    return DAY_CLASS;
  }

  /**
   * Get current theme (stored > system > default day)
   */
  function getCurrentTheme() {
    const stored = getStoredTheme();
    if (stored === DAY_CLASS || stored === NIGHT_CLASS) {
      return stored;
    }
    return getSystemPreference();
  }

  /**
   * Apply theme to document
   */
  function applyTheme(theme, animate = false) {
    const html = document.documentElement;
    const body = document.body;

    if (animate) {
      // Add transition class for smooth color changes
      html.classList.add(TRANSITION_CLASS);
      body.classList.add(TRANSITION_CLASS);
    }

    // Remove both classes first
    html.classList.remove(DAY_CLASS, NIGHT_CLASS);

    // Add new theme class
    html.classList.add(theme);

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', theme === NIGHT_CLASS ? '#0F172A' : '#1E3A5F');
    }

    // Dispatch custom event for other scripts to listen to
    window.dispatchEvent(new CustomEvent('themechange', {
      detail: { theme: theme }
    }));

    if (animate) {
      // Remove transition class after animation completes
      setTimeout(function() {
        html.classList.remove(TRANSITION_CLASS);
        body.classList.remove(TRANSITION_CLASS);
      }, TRANSITION_DURATION);
    }
  }

  /**
   * Toggle between day and night themes
   */
  function toggleTheme() {
    const current = getCurrentTheme();
    const newTheme = current === DAY_CLASS ? NIGHT_CLASS : DAY_CLASS;

    storeTheme(newTheme);
    applyTheme(newTheme, true);

    // Update all toggle buttons
    updateToggleButtons(newTheme);

    return newTheme;
  }

  /**
   * Update all toggle button states
   */
  function updateToggleButtons(theme) {
    const buttons = document.querySelectorAll('.theme-toggle');

    buttons.forEach(function(button) {
      const isNight = theme === NIGHT_CLASS;

      // Update aria-label for accessibility
      button.setAttribute('aria-label',
        isNight ? 'Switch to day mode' : 'Switch to night mode'
      );

      // Update aria-pressed
      button.setAttribute('aria-pressed', isNight ? 'true' : 'false');

      // Update toggle track position
      const track = button.querySelector('.toggle-track');
      if (track) {
        track.setAttribute('data-theme', theme);
      }

      // Update icons visibility
      const sunIcon = button.querySelector('.sun-icon');
      const moonIcon = button.querySelector('.moon-icon');

      if (sunIcon && moonIcon) {
        if (isNight) {
          sunIcon.style.opacity = '0';
          sunIcon.style.transform = 'scale(0.5) rotate(-90deg)';
          moonIcon.style.opacity = '1';
          moonIcon.style.transform = 'scale(1) rotate(0deg)';
        } else {
          sunIcon.style.opacity = '1';
          sunIcon.style.transform = 'scale(1) rotate(0deg)';
          moonIcon.style.opacity = '0';
          moonIcon.style.transform = 'scale(0.5) rotate(90deg)';
        }
      }

      // Update knob position
      const knob = button.querySelector('.toggle-knob');
      if (knob) {
        knob.style.transform = isNight ? 'translateX(28px)' : 'translateX(0)';
      }
    });
  }

  /**
   * Initialize theme toggle buttons
   */
  function initializeToggleButtons() {
    const buttons = document.querySelectorAll('.theme-toggle');
    const currentTheme = getCurrentTheme();

    buttons.forEach(function(button) {
      // Set initial state
      updateToggleButtons(currentTheme);

      // Add click handler
      button.addEventListener('click', function(e) {
        e.preventDefault();
        toggleTheme();
      });

      // Add keyboard support
      button.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleTheme();
        }
      });
    });
  }

  /**
   * Listen for system preference changes
   */
  function watchSystemPreference() {
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      // Use addEventListener if supported, else addListener (for older Safari)
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', function(e) {
          // Only auto-switch if user hasn't set a preference
          if (!getStoredTheme()) {
            const newTheme = e.matches ? NIGHT_CLASS : DAY_CLASS;
            applyTheme(newTheme, true);
            updateToggleButtons(newTheme);
          }
        });
      } else if (mediaQuery.addListener) {
        mediaQuery.addListener(function(e) {
          if (!getStoredTheme()) {
            const newTheme = e.matches ? NIGHT_CLASS : DAY_CLASS;
            applyTheme(newTheme, true);
            updateToggleButtons(newTheme);
          }
        });
      }
    }
  }

  /**
   * Initialize everything when DOM is ready
   */
  function init() {
    // Apply theme immediately (should already be done in head, but ensure consistency)
    applyTheme(getCurrentTheme(), false);

    // Setup toggle buttons
    initializeToggleButtons();

    // Watch for system preference changes
    watchSystemPreference();
  }

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose API globally for other scripts
  window.CauseProTheme = {
    toggle: toggleTheme,
    get: getCurrentTheme,
    set: function(theme) {
      if (theme === DAY_CLASS || theme === NIGHT_CLASS) {
        storeTheme(theme);
        applyTheme(theme, true);
        updateToggleButtons(theme);
      }
    },
    DAY: DAY_CLASS,
    NIGHT: NIGHT_CLASS
  };

})();
